/**
 * The username-and-password form of the sign-in pages. Rendered by `../Login.tsx` on `/login`,
 * and by `../EasyQuoteHelpers/EasyQuote.Components.tsx` → `EasyQuoteSteps` as the "Log In" step
 * of `/easy-quote` (`inEasyQuoteFlow`, which adds a Back button, centres the layout and doesn't
 * scroll to errors).
 *
 * The site header has a second sign-in form: `@ryerson/frontend.header-footer` →
 * `Renderers/LoginButtonRenderer.tsx` → `LoginButton` opens that package's `Login/LoginForm.tsx`
 * in a popover on non-touch devices, and it signs in where the user is, without going to
 * `/login`. It differs from this form in two ways: it reads the API's error text from
 * `error.response.data`, and it remembers the username in localStorage `rememberedUserName`.
 *
 * Sign-in: `useApplication().login` (`@ryerson/frontend.application` → `Authentication.tsx` →
 * `login`) posts `{ user, pass }` to `ApiUrl.LOGIN`, `/v1/users/v1/public/token/auth`
 * (`web-users-api/application/src/functions/PostPublicAuth/postPublicAuth.ts`). On a 200 it
 * decodes the returned JWT into the signed-in user, which the package keeps in localStorage. The
 * shared axios interceptor lets a 401 from this route through to the form instead of redirecting
 * to `/login`.
 *
 * Rules a change must keep:
 * - **The `redirectTo` hand-off** (web-architecture ADR-0035). After a successful sign-in,
 *   {@link LoginForm} → `onLoginClick` replaces the location with sessionStorage `redirectTo`, a
 *   site-root path saved by `components/NextLayouts/ProtectedRoute.tsx`, its web-store-ui twin or
 *   the header's sign-in button, and then removes the key. Without one it goes to `/`. It is a
 *   full page load, so a `/store/...` target is served by web-store-ui. The key name and the
 *   value's shape are shared with those writers; see `ProtectedRoute.tsx`.
 * - The default differs from the already-signed-in case: `../hooks/useRedirectIfLoggedIn.ts`
 *   sends a signed-in visitor of `/login` to `redirectTo`, otherwise `/store/dashboard` in
 *   production, while this form falls back to `/`.
 * - Never log the sign-in error or request (web-architecture ADR-0015 amendment, rule 2): on a
 *   network failure the axios error's `config.data` still holds the password.
 *
 * Errors are shown by `Login.Components.tsx` → `ErrorMessage`: the validation message for an
 * empty field, otherwise an error chosen by the response text (see `onLoginClick`).
 */
import React from "react";
import { css } from "@emotion/react";
import { useTheme } from "@ryerson/frontend.theme";
import { useApplication, User } from "@ryerson/frontend.application";
import { UserLoginRequest } from "@ryerson/frontend.application";
import { Flex, FlexItem } from "@ryerson/frontend.layout";
import { Icon } from "@ryerson/frontend.assets";
import { Button } from "@ryerson/frontend.button";
import { Typography } from "@ryerson/frontend.typography";
import { Link } from "@ryerson/frontend.navigation";
import { Checkbox, Input } from "@ryerson/frontend.form-controls";
import { CONSTANTS } from "@ryerson/frontend.common";
import { LoginContent } from "./LoginContent";
import { isLoginInputValid, checkAllInputsValid, LoginInputFields } from "./Login.Functions";
import { LoginAlertMessage, ErrorMessage } from "./Login.Components";
import { Alert } from "@ryerson/frontend.notification";
import { scrollToMessage } from "../EasyQuoteHelpers/EasyQuote.Functions";
import { DisplaySteps } from "../EasyQuoteHelpers/EasyQuote.types";

/** Props of {@link LoginForm}. */
export interface LoginProps {
	className?: string;
	isMobile: boolean;
	// Info alert above the fields, for example the "account already exists" note from registration.
	message?: string;
	// Called with `true` after a successful sign-in, before the redirect. No caller passes it.
	handleClose?: (status?: boolean) => void;
	// Not read: the destination comes from sessionStorage `redirectTo`.
	redirectTo?: string;
	// True for the Easy Quote "Log In" step.
	inEasyQuoteFlow?: boolean;
	// Easy Quote only: the Back button sets the step to `"launcher"`.
	setDisplayStep?: React.Dispatch<React.SetStateAction<DisplaySteps>>;
}

/** The sign-in request body's field names. No file imports this type. */
export interface Login {
	user: string;
	pass: string;
}

/** The sign-in form; see the file header. */
const LoginForm: React.FC<LoginProps> = ({
	className = "",
	isMobile,
	handleClose,
	message,
	inEasyQuoteFlow = false,
	setDisplayStep,
}) => {
	const { theme } = useTheme();
	const [userName, setUserName] = React.useState<string>("");
	const [password, setPassword] = React.useState<string>("");
	const [showPassword, setShowPassword] = React.useState<boolean>(false);
	// Bound to the "Remember my username" checkbox. Nothing reads it: the username is not stored.
	const [remember, setRemember] = React.useState<boolean>(false);
	const [submitted, setSubmitted] = React.useState<boolean>(false);
	const [errorType, setErrorType] = React.useState<"validationError" | "apiError" | null>(null);
	const [apiErrorMessage, setApiErrorMessage] = React.useState<string>("");
	const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
	const {
		localization: { language },
		login,
	} = useApplication();

	const {
		continueText,
		emailText,
		passwordText,
		passwordHideText,
		passwordShowText,
		rememberMyUsernameText,
		forgotPasswordText,
		resetPasswordText,
		loginText,
		back,
	} = LoginContent[language];

	/**
	 * Validates both fields, signs in, and on success leaves the page for sessionStorage
	 * `redirectTo` or `/` (the hand-off in the file header).
	 *
	 * In the Easy Quote flow nothing sets `redirectTo`, so this goes to `/` unless an earlier
	 * protected-page visit in the tab left one. Signing in there also re-renders
	 * `pages/easy-quote/index.tsx`, whose `useRedirectIfLoggedInEasyQuote` sets
	 * `window.location.href` to `/store/quotes/<quoteId>`, so two navigations are issued. The
	 * later one, the hook's, is the one the browser completes: the customer lands on the quote,
	 * not on `/`. That outcome rests on the hook; without it this flow would end on `/`.
	 *
	 * On failure it shows `ErrorMessage` with `error.data` as the message. `login` rejects with an
	 * `AxiosError` (or an `Error` for a non-200 success status), and neither has a `data` field:
	 * the Users API's text (`Invalid`, `Reset`, `Contact Support`, see `@enums/common.enums` →
	 * `LoginErrorResponse`) is at `error.response.data`. So the message is `undefined`, and
	 * `ErrorMessage` shows the generic `AUTH_ERROR` for every rejected sign-in.
	 */
	const onLoginClick = () => {
		setSubmitted(true);

		const isValid = checkAllInputsValid(userName, password);

		if (!isValid) {
			setErrorType("validationError");
			!inEasyQuoteFlow && scrollToMessage("login-error-message", 300);
			return;
		}

		setErrorType(null);
		setIsSubmitting(true);

		const userLoginRequest: UserLoginRequest = {
			username: userName,
			password: password,
		};
		login(userLoginRequest)
			.then((user: User) => {
				handleClose && handleClose(true);

				let redirectTo = sessionStorage.getItem("redirectTo");

				// A full load, so `/store` targets reach web-store-ui; `replace` also drops the
				// sign-in page from history.
				window.location.replace(redirectTo || "/");

				!!sessionStorage && !!redirectTo && sessionStorage.removeItem("redirectTo");

				!!sessionStorage && sessionStorage.removeItem("displayStep");

				setApiErrorMessage("");
			})
			.catch((error: any) => {
				// `error.data` is undefined for the errors `login` rejects with; see the doc above.
				!inEasyQuoteFlow && scrollToMessage("login-error-message", 300);
				setErrorType("apiError");
				setApiErrorMessage(error ? error.data : "error");
				setIsSubmitting(false);
			});
	};

	// Enter in either field submits.
	const handleKeypress = (e: any) => {
		if (e.keyCode === 13) {
			onLoginClick();
		}
	};
	const handlePasswordVisibility = () => {
		setShowPassword(!showPassword);
	};

	return (
		<FlexItem
			className={className}
			css={{
				width: "100%",
				padding: isMobile ? "0px" : "0px 35px 0px 20px",
			}}
		>
			<Flex direction="column" className="login-header">
				<Typography
					variant={isMobile ? "h3" : "h4"}
					css={{
						alignSelf: `${inEasyQuoteFlow ? "center" : "unset"}`,
						marginTop: isMobile ? "12px" : inEasyQuoteFlow ? "0px" : "44px",
						paddingBottom: errorType ? "0px" : "24px",
					}}
				>
					{loginText}
				</Typography>
			</Flex>
			<div id="login-error-message">
				<LoginAlertMessage message={message} severity="info" isMobile={isMobile} />
				<ErrorMessage
					errorType={errorType}
					apiErrorMessage={apiErrorMessage}
					isMobile={isMobile}
				/>
			</div>
			<Flex
				className="login-inputs-wrapper"
				direction="column"
				css={{ gap: 26, width: "100%" }}
			>
				<Input
					name="username"
					id="username"
					type="primary"
					label={emailText}
					placeholder={""}
					value={userName}
					onChange={(event) => setUserName(event.target.value)}
					onKeyDown={handleKeypress}
					language={language}
					required={true}
					submitted={submitted}
					isValid={
						submitted
							? isLoginInputValid({
									inputName: LoginInputFields.userName,
									value: userName,
								})
							: true
					}
				/>
				<Input
					name="password"
					id="password"
					type="primary"
					label={passwordText}
					inputType={showPassword ? "text" : "password"}
					icon={showPassword ? "no-view" : "view"}
					iconLabel={showPassword ? passwordHideText : passwordShowText}
					placeholder={""}
					value={password}
					onChange={(event) => setPassword(event.target.value)}
					onKeyDown={handleKeypress}
					onIconClick={handlePasswordVisibility}
					language={language}
					required={true}
					submitted={submitted}
					isValid={
						submitted
							? isLoginInputValid({
									inputName: LoginInputFields.password,
									value: password,
								})
							: true
					}
				/>
			</Flex>

			<Flex
				direction={isMobile ? "column" : "row"}
				justifyContent="space-between"
				
				css={{
					gap: 20,
					marginTop: isMobile ? "20px" : "24px",
					marginBottom: isMobile ? "44px" : `${inEasyQuoteFlow ? "34px" : "92px"}`,
				}}
			>
				<Checkbox
					checked={remember}
					size="sm"
					label={rememberMyUsernameText}
					onChange={() => setRemember(!remember)}
					id="id"
					value={"value"}
					language={language}
				/>
				<Link type="secondary" to="/login/forgot-password" size="sm">
					{forgotPasswordText}
				</Link>
			</Flex>
			<Flex
				style={css`
					gap: 20px;
					padding-bottom: ${inEasyQuoteFlow ? "0px" : "60px"};
					justify-content: ${inEasyQuoteFlow ? "center" : "flex-start"};
				`}
				direction={isMobile ? "column" : "row"}
			>
				{inEasyQuoteFlow ? (
					<Button
						dataTestId="easy-quote-back-button"
						css={css`
							width: ${isMobile ? "100%" : "160px"};
						`}
						label={back}
						onClick={() => {
							!!setDisplayStep && setDisplayStep(() => "launcher");
						}}
						background={theme.colors.primary.darkGray}
						foreground="light"
						fullwidth={true}
					/>
				) : null}
				<Button
					dataTestId="login-submit-button"
					css={css`
						width: ${isMobile ? "100%" : "160px"};
					`}
					label={inEasyQuoteFlow ? continueText : loginText}
					onClick={onLoginClick}
					disabled={isSubmitting}
					fullwidth={true}
				/>
			</Flex>
		</FlexItem>
	);
};

export default LoginForm;
